import React, { useEffect, useRef, useState } from 'react';
import { Maximize2, X } from 'lucide-react';
import {
  LiveViewersPayload, LiveViewerProfile, LiveChatMessage, ReactionCounts,
  REACTION_KEYS, REACTION_EMOJI, UPLOADS_BASE, isEmoOnlyClient
} from '../../services/liveService';

const fotoSrc = (url?: string | null) => {
  if (!url) return '';
  return /^https?:\/\//.test(url) ? url : `${UPLOADS_BASE}${url}`;
};

const initialsOf = (v: LiveViewerProfile) =>
  v.initials || (v.name || '··').slice(0, 2).toUpperCase();

const Avatar: React.FC<{ v: LiveViewerProfile }> = ({ v }) => {
  const cp = v.communityProfile;
  const url =
    (v.kind === 'representative' && v.foto && fotoSrc(v.foto)) ||
    (cp?.avatarType === 'image' && cp.avatarImage ? fotoSrc(cp.avatarImage) : '');

  if (url) {
    return (
      <div className="av" title={v.name} style={{ background: '#000' }}>
        <img src={url} alt={v.name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
      </div>
    );
  }
  const bg = cp?.avatarColor;
  return (
    <div className="av" title={v.name} style={bg ? { background: bg, color: '#fff' } : undefined}>
      {initialsOf(v)}
    </div>
  );
};

// ===== Chat avatar (per messaggi) =====
const ChatAvatar: React.FC<{ msg: LiveChatMessage }> = ({ msg }) => {
  const a = msg.author;
  const cp = a.communityProfile;
  const url =
    (a.userType === 'representative' && a.foto && fotoSrc(a.foto)) ||
    (cp?.avatarType === 'image' && cp.avatarImage ? fotoSrc(cp.avatarImage) : '');
  if (url) {
    return (
      <div className="av" style={{ background: '#000' }}>
        <img src={url} alt={a.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
      </div>
    );
  }
  const bg = cp?.avatarColor;
  return (
    <div className="av" style={bg ? { background: bg, color: '#fff' } : undefined}>
      {a.initials || (a.name || '··').slice(0, 2).toUpperCase()}
    </div>
  );
};

// ===== ChatPanel =====
interface ChatPanelProps {
  messages: LiveChatMessage[];
  isAuthenticated: boolean;
  onSend: (text: string) => Promise<void> | void;
  /** Se definito, mostra il bottone espandi/riduci (solo rail desktop). */
  onToggleExpand?: () => void;
  expanded?: boolean;
}

export const LiveChatPanel: React.FC<ChatPanelProps> = ({ messages, isAuthenticated, onSend, onToggleExpand, expanded = false }) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  // Fix #13: scrolla a fondo SOLO se l'utente e\` gia\` vicino al fondo (pattern chat standard).
  const isAtBottomRef = useRef(true);

  const lastMsgId = messages[messages.length - 1]?._id;

  useEffect(() => {
    if (!bodyRef.current) return;
    if (isAtBottomRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [lastMsgId, messages.length]);

  const onScroll = () => {
    const el = bodyRef.current;
    if (!el) return;
    isAtBottomRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < 60;
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await onSend(t);
      setText('');                          // svuota SOLO se onSend non ha thrown
      isAtBottomRef.current = true;         // scrollback al proprio msg
    } catch (_) {
      // l'errore e\` gia\` toastato dal parent; lasciamo l'input intatto
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="panel">
      <div className="panel-head">
        <h3><span className="dot" /> Chat associati</h3>
        <div className="panel-head-right">
          <span className="pill">Live</span>
          {onToggleExpand && (
            <button
              type="button"
              className="chat-expand-btn"
              onClick={onToggleExpand}
              aria-label={expanded ? 'Riduci chat' : 'Espandi chat a tutta larghezza'}
              title={expanded ? 'Riduci' : 'Espandi a tutta larghezza'}
            >
              {expanded ? <X width={16} height={16} /> : <Maximize2 width={15} height={15} />}
            </button>
          )}
        </div>
      </div>
      <div className="chat-body" ref={bodyRef} onScroll={onScroll}>
        {messages.length === 0 ? (
          <div style={{ alignSelf: 'center', color: 'var(--un-ink-400)', fontSize: 13, padding: '40px 0' }}>
            Nessun messaggio. Inizia la conversazione!
          </div>
        ) : (
          messages.map(m => {
            const isMod = m.author.userType === 'representative';
            // Fix M9 ciclo 2: ricalcola emoOnly client-side (la regex backend cambia nel tempo;
            // i messaggi storici hanno flag desincronizzato. Recompute = fonte di verita\` viva).
            const emoOnly = isEmoOnlyClient(m.text);
            return (
              <div
                key={m._id}
                className={'chat-msg ' + (isMod ? 'is-mod ' : '') + (emoOnly ? 'is-emo ' : '')}
              >
                <ChatAvatar msg={m} />
                <div className="bub">
                  {!emoOnly && (
                    <div className="nm">
                      {m.author.name}
                      <small>· {new Date(m.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</small>
                    </div>
                  )}
                  {!emoOnly && m.author.attore?.nome && (
                    <div className="persona" data-testid="persona-dipendente">{m.author.attore.nome}</div>
                  )}
                  <div className="tx">{m.text}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <form className="chat-input" onSubmit={handleSend}>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={isAuthenticated ? 'Scrivi un messaggio…' : 'Accedi come associato per partecipare'}
          disabled={!isAuthenticated || sending}
          maxLength={500}
        />
        <button
          type="submit"
          className="send"
          disabled={!isAuthenticated || sending || !text.trim()}
          aria-label="Invia"
        >
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </form>
    </div>
  );
};

// ===== ReactionsPanel =====
interface ReactionsPanelProps {
  counts: ReactionCounts;
}

export const LiveReactionsPanel: React.FC<ReactionsPanelProps> = ({ counts }) => {
  const total = REACTION_KEYS.reduce((s, k) => s + (counts[k] || 0), 0);
  const sorted = [...REACTION_KEYS].sort((a, b) => (counts[b] || 0) - (counts[a] || 0));

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>Reazioni live</h3>
        <span className="pill">{total.toLocaleString('it-IT')}</span>
      </div>
      <div className="top-rxn">
        {sorted.map(k => {
          const n = counts[k] || 0;
          const pct = total > 0 ? Math.max(2, Math.round((n / total) * 100)) : 0;
          return (
            <div className="row" key={k}>
              <span className="emo">{REACTION_EMOJI[k]}</span>
              <span className="bar"><i style={{ width: pct + '%' }} /></span>
              <span className="n">{n.toLocaleString('it-IT')}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ===== ViewersPanel =====
interface ViewersProps {
  viewers?: LiveViewersPayload;
}

export const LiveViewersPanel: React.FC<ViewersProps> = ({ viewers }) => {
  const count = viewers?.count ?? 0;
  const recent = viewers?.recent || [];
  const visible = recent.slice(0, 15);
  const overflow = Math.max(0, count - visible.length);

  return (
    <div className="panel">
      <div className="panel-head">
        <h3><span className="dot" /> In ascolto ora</h3>
        <span className="pill">{count.toLocaleString('it-IT')}</span>
      </div>
      <div className="viewers-grid">
        {visible.map((v, i) => <Avatar key={i} v={v} />)}
        {visible.length === 0 && (
          <div className="av more" style={{ gridColumn: '1 / -1', aspectRatio: 'auto', padding: '8px 4px', borderRadius: 6, fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500 }}>
            Nessuno spettatore identificato
          </div>
        )}
        {overflow > 0 && <div className="av more">+{overflow.toLocaleString('it-IT')}</div>}
      </div>
    </div>
  );
};

// ===== Rail =====
interface LiveRailProps {
  viewers?: LiveViewersPayload;
  chat: LiveChatMessage[];
  isAuthenticated: boolean;
  onSendChat: (text: string) => Promise<void> | void;
  reactionsCounts: ReactionCounts;
  chatExpanded?: boolean;
  onToggleChatExpand?: () => void;
}

const LiveRail: React.FC<LiveRailProps> = ({ viewers, chat, isAuthenticated, onSendChat, reactionsCounts, chatExpanded, onToggleChatExpand }) => (
  <aside className="rail">
    {/* Quando espansa, la chat e' renderizzata a tutta larghezza fuori dalla rail */}
    {!chatExpanded && (
      <LiveChatPanel messages={chat} isAuthenticated={isAuthenticated} onSend={onSendChat} onToggleExpand={onToggleChatExpand} />
    )}
    <LiveReactionsPanel counts={reactionsCounts} />
    <LiveViewersPanel viewers={viewers} />
  </aside>
);

export default LiveRail;
